<script setup lang="ts">
import { computed, nextTick, ref, type PropType } from 'vue';
import { IconCheck, IconClose, IconEdit } from '@/components/design/icons';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { gaiaNeutral, gaiaStatus } from '@/components/design/themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard';
import { logger } from '@/utils/logger';
import EntityFieldInput from './EntityFieldInput.vue';
import {
  rowValues,
  toText,
  updateEntity,
  validateValues,
  type EntityField,
  type EntityFormValues,
  type EntityListConfig,
  type EntityRow,
} from './entityListConfig';

/**
 * One value on the detail page, editable where it stands: click it (or focus
 * it and press Enter) to swap in the field's own control. A select saves the
 * moment a value is picked; anything else saves on Enter or ✓ and cancels on
 * Esc or ✕. The save sends the whole record with this one field changed —
 * the same body the drawer would — so kind-aware updates keep working.
 */
const props = defineProps({
  config: { type: Object as PropType<EntityListConfig>, required: true },
  row: { type: Object as PropType<EntityRow>, required: true },
  field: { type: Object as PropType<EntityField>, required: true },
  /** The value as the page reads it ("Germany", not "DEU"). */
  text: { type: String, required: true },
  empty: { type: Boolean, default: false },
  /**
   * Why the caller can't edit this record (`deniedReason` on its bits); null
   * when they can. A blocked value reads as plain text with the reason as its
   * title — no wash, no pencil, nothing to click.
   */
  blockedReason: { type: String as PropType<string | null>, default: null },
});

const emit = defineEmits<{ saved: [] }>();

const message = useGaiaToast();

const editing = ref(false);
const editor = ref<HTMLElement | null>(null);
const draft = ref<EntityFormValues>({});
const error = ref<string | null>(null);
const saving = ref(false);

/** A select commits on pick; the rest wait for Enter / ✓. */
const savesOnPick = computed(() => props.field.kind === 'select');

/** The stored value, as the editor holds it — what "changed" is measured against. */
const original = computed(() => rowValues(props.config, props.row)[props.field.key]);
/** The draft differs from what's saved; typing the old value back clears it. */
const dirty = computed(
  () =>
    editing.value && JSON.stringify(original.value) !== JSON.stringify(draft.value[props.field.key])
);
const multiline = computed(() => props.field.kind === 'textarea');
/** Linked records, each opening its own page; clicking beside them still edits. */
const links = computed(() => props.field.links?.(props.row) ?? []);

// A value typed but not yet saved with Enter / ✓ is easy to walk away from.
useUnsavedChangesGuard(() => dirty.value, `Changes to ${props.field.label}`);

function start(): void {
  if (saving.value || props.blockedReason) return;
  draft.value = rowValues(props.config, props.row);
  error.value = null;
  editing.value = true;
  // Straight into the control the click asked for.
  void nextTick(() => editor.value?.querySelector<HTMLElement>('input, textarea')?.focus());
}

function cancel(): void {
  editing.value = false;
  error.value = null;
}

function onInput(value: string | string[]): void {
  draft.value = { ...draft.value, [props.field.key]: value };
  error.value = null;
  if (savesOnPick.value) void save();
}

async function save(): Promise<void> {
  if (!dirty.value) {
    cancel();
    return;
  }
  const errors = validateValues(props.config, draft.value);
  // A cross-field rule may report under a sibling key (a person's name parts).
  const firstError = errors[props.field.key] ?? Object.values(errors)[0];
  if (firstError) {
    error.value = firstError;
    return;
  }
  saving.value = true;
  try {
    // The update sends the whole record, so only this field comes from the
    // draft: the rest is read now, not when editing started — another field
    // saved in the meantime would otherwise be written back with its old value.
    // ponytail: a second save before the parent's refetch lands still sees the
    // old row; merge the saved value into the parent's row if that bites.
    await updateEntity(
      props.config,
      props.row.uid,
      {
        ...rowValues(props.config, props.row),
        [props.field.key]: draft.value[props.field.key],
      },
      props.row
    );
    message.success(`${props.field.label} updated`);
    editing.value = false;
    emit('saved');
  } catch (saveError) {
    logger.error('Error saving field:', saveError);
    error.value = saveError instanceof Error ? saveError.message : 'Couldn’t save this change';
  } finally {
    saving.value = false;
  }
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.stopPropagation();
    cancel();
  } else if (event.key === 'Enter' && !multiline.value && !savesOnPick.value) {
    event.preventDefault();
    void save();
  }
}

const hoverSurface = gaiaNeutral.surfaceHover;
const unsavedMark = gaiaStatus.warning;
</script>

<template>
  <div v-if="editing" ref="editor" class="inline-editor" :class="{ dirty }" @keydown="onKeydown">
    <n-flex align="flex-start" :size="6" :wrap="false">
      <div :style="{ flex: 1, minWidth: 0 }">
        <entity-field-input
          :field="field"
          :value="draft[field.key] ?? ''"
          :error="Boolean(error)"
          :seed-options="field.selectedOptions?.(row) ?? []"
          @update:value="onInput"
        />
      </div>
      <template v-if="!savesOnPick">
        <gaia-icon-button :dimension="34" title="Save" :disabled="saving" @click="save">
          <n-icon :component="IconCheck" :size="16" />
        </gaia-icon-button>
        <gaia-icon-button :dimension="34" quaternary title="Cancel" @click="cancel">
          <n-icon :component="IconClose" :size="16" />
        </gaia-icon-button>
      </template>
    </n-flex>
    <gaia-text
      v-if="dirty && !error"
      size="micro"
      :style="{ display: 'block', marginTop: '4px', color: unsavedMark }"
    >
      Unsaved — Enter or ✓ to save, Esc to discard
    </gaia-text>
    <gaia-text
      v-if="error"
      size="micro"
      type="error"
      :style="{ display: 'block', marginTop: '4px' }"
    >
      {{ error }}
    </gaia-text>
  </div>

  <div
    v-else
    class="inline-value"
    :class="{ blocked: blockedReason }"
    :role="blockedReason ? undefined : 'button'"
    :tabindex="blockedReason ? undefined : 0"
    :title="blockedReason ?? `Edit ${field.label.toLowerCase()}`"
    @click="start"
    @keydown.enter.prevent="start"
  >
    <gaia-text
      size="body"
      :tone="empty ? 'muted' : 'ink'"
      :style="{ lineHeight: 1.55, whiteSpace: 'pre-wrap', minWidth: 0 }"
    >
      <template v-if="saving">Saving…</template>
      <template v-else-if="links.length">
        <template v-for="(link, index) in links" :key="index">
          <template v-if="index">, </template>
          <gaia-link :to="link.to" :underline="false" @click.stop>{{ link.label }}</gaia-link>
        </template>
      </template>
      <template v-else>{{ toText(text) }}</template>
    </gaia-text>
    <n-icon v-if="!blockedReason" :component="IconEdit" :size="14" class="inline-pencil" />
  </div>
</template>

<style scoped>
/* A thin amber rule down the left edge while the draft differs from the saved
   value — so a half-finished edit can't be mistaken for a saved one. */
.inline-editor {
  margin-left: -10px;
  padding-left: 8px;
  border-left: 2px solid transparent;
  transition: border-color 0.15s ease;
}

.inline-editor.dirty {
  border-left-color: v-bind(unsavedMark);
}

/* The value reads as text until pointed at; the wash and pencil say "editable". */
.inline-value {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: -4px -8px;
  padding: 4px 8px;
  border-radius: 6px;
  cursor: text;
}

.inline-value:not(.blocked):hover,
.inline-value:not(.blocked):focus-visible {
  background: v-bind(hoverSurface);
  outline: none;
}

.inline-value.blocked {
  cursor: default;
}

.inline-pencil {
  flex: none;
  margin-top: 4px;
  margin-left: auto;
  opacity: 0;
}

.inline-value:hover .inline-pencil,
.inline-value:focus-visible .inline-pencil {
  opacity: 0.6;
}
</style>
